<template>
    <div class="company-management-wrapper">
        <div class="breadcrumb">
            <router-link to="/">Dashboard</router-link>
            <span class="crumb-sep">/</span>
            <span class="crumb-current">Payment Links</span>
        </div>

        <div class="management-header">
            <div>
                <h1 class="page-title">Payment Links</h1>
                <p class="page-subtitle">Track every Razorpay purchase link generated for prospects.</p>
            </div>
            <select v-model="statusFilter" class="status-filter" @change="fetchLinks(1)">
                <option value="">All Statuses</option>
                <option value="PENDING_FORM">Pending Form</option>
                <option value="PENDING_PAYMENT">Pending Payment</option>
                <option value="PAID">Paid</option>
                <option value="FAILED">Failed</option>
                <option value="EXPIRED">Expired</option>
            </select>
        </div>

        <div class="table-glass-card">
            <table class="premium-table">
                <thead>
                    <tr>
                        <th>Company Name</th>
                        <th>Admin Email</th>
                        <th>Plan</th>
                        <th>Amount</th>
                        <th>Status</th>
                        <th>Created</th>
                        <th class="text-right">Actions</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="link in links" :key="link.id" class="table-row-hover">
                        <td class="cell-primary">{{ link.company_name || '—' }}</td>
                        <td class="cell-secondary">{{ link.admin_contact_email || '—' }}</td>
                        <td class="cell-secondary">{{ link.plan?.name || '—' }}</td>
                        <td class="cell-secondary">₹{{ formatPrice(link.amount) }}</td>
                        <td>
                            <span class="status-badge" :class="statusClass(link.status)">
                                <i class="status-dot"></i>
                                {{ statusLabel(link.status) }}
                            </span>
                        </td>
                        <td class="cell-secondary">{{ formatDate(link.created_date) }}</td>
                        <td>
                            <div class="row-actions">
                                <button
                                    v-if="link.status === 'PENDING_FORM' || link.status === 'PENDING_PAYMENT'"
                                    class="action-btn"
                                    title="Copy purchase link"
                                    @click="copyLink(link)"
                                >
                                    {{ copiedId === link.id ? '✓' : 'Copy' }}
                                </button>
                                <router-link
                                    v-if="link.status === 'PAID' && link.provisioned_company_id"
                                    :to="'/companies/' + link.provisioned_company_id"
                                    class="action-btn"
                                    title="View company"
                                >
                                    View
                                </router-link>
                            </div>
                        </td>
                    </tr>
                    <tr v-if="links.length === 0">
                        <td colspan="7" class="empty-state">
                            <p>No payment links generated yet.</p>
                        </td>
                    </tr>
                </tbody>
            </table>
            <AppPagination :currentPage="currentPage" :totalPages="totalPages" @page-changed="fetchLinks" />
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import AppPagination from './AppPagination.vue';

export default {
    name: 'PaymentLinks',
    components: { AppPagination },
    data() {
        return {
            links: [],
            currentPage: 1,
            totalPages: 1,
            statusFilter: '',
            copiedId: null,
        }
    },
    async mounted() {
        await this.fetchLinks();
    },
    methods: {
        authHeaders() {
            return { Authorization: `Bearer ${localStorage.getItem('super_admin_token')}` };
        },
        async fetchLinks(page = 1) {
            try {
                this.currentPage = page;
                const params = { page, page_size: 10 };
                if (this.statusFilter) params.status = this.statusFilter;
                const response = await axios.get(`${this.$API_URL}/payment/links`, {
                    params,
                    headers: this.authHeaders()
                });
                this.links = response.data.items || [];
                this.totalPages = response.data.total_pages || 1;
            } catch (error) {
                console.error('Error fetching payment links:', error);
            }
        },
        formatPrice(amount) {
            return ((amount || 0) / 100).toLocaleString('en-IN');
        },
        formatDate(dateStr) {
            if (!dateStr) return '—';
            const d = new Date(dateStr);
            return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
        },
        statusClass(status) {
            if (status === 'PAID') return 'active';
            if (status === 'PENDING_PAYMENT' || status === 'PENDING_FORM') return 'expiring';
            return 'inactive';
        },
        statusLabel(status) {
            const map = {
                PENDING_FORM: 'Pending Form',
                PENDING_PAYMENT: 'Pending Payment',
                PAID: 'Paid',
                FAILED: 'Failed',
                EXPIRED: 'Expired',
            };
            return map[status] || status;
        },
        async copyLink(link) {
            try {
                await navigator.clipboard.writeText(link.purchase_url);
                this.copiedId = link.id;
                setTimeout(() => { this.copiedId = null; }, 2000);
            } catch (error) {
                console.error('Copy failed:', error);
            }
        }
    }
}
</script>

<style scoped>
.company-management-wrapper {
    font-family: var(--font-sans);
}

.breadcrumb {
    margin-bottom: 1.5rem;
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: var(--fs-sm);
}

.breadcrumb a { color: var(--text-secondary); text-decoration: none; font-weight: 500; }
.breadcrumb a:hover { color: #0071C5; }
.crumb-sep { color: #cbd5e1; }
.crumb-current { color: #0071C5; font-weight: 700; }

.management-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    flex-wrap: wrap;
    gap: 1rem;
    margin-bottom: 2rem;
}

.page-title {
    margin: 0;
    font-family: var(--font-display);
    font-size: var(--fs-h1);
    line-height: var(--lh-h1);
    font-weight: var(--fw-h1);
    color: var(--text-primary);
    letter-spacing: -0.02em;
}

.page-subtitle {
    color: var(--text-secondary);
    margin: var(--space-heading-body) 0 0;
    font-size: var(--fs-sm);
}

.status-filter {
    padding: 0.7rem 1rem;
    border: 1.5px solid #e2e8f0;
    border-radius: 12px;
    font-size: 0.85rem;
    font-family: var(--font-sans);
    background: white;
    color: var(--text-primary);
    cursor: pointer;
}

.table-glass-card {
    background: white;
    border-radius: 20px;
    border: 1px solid #eef2f7;
    overflow: hidden;
    box-shadow: 0 6px 20px rgba(15, 23, 42, 0.04);
}

.premium-table { width: 100%; border-collapse: collapse; }

.premium-table thead { background: #f8fafc; border-bottom: 1px solid #e2e8f0; }

.premium-table th {
    padding: 1.1rem 1.5rem;
    text-align: left;
    color: var(--text-secondary);
    font-size: var(--fs-th);
    font-weight: var(--fw-th);
    text-transform: uppercase;
    letter-spacing: var(--tracking-th);
}

.premium-table th.text-right { text-align: right; }

.premium-table td {
    padding: 1.1rem 1.5rem;
    border-bottom: 1px solid #f1f5f9;
}

.table-row-hover { transition: background 0.2s; }
.table-row-hover:hover { background: #f8fafc; }

.cell-primary { color: var(--text-primary); font-size: var(--fs-body); font-weight: 500; }
.cell-secondary { color: var(--text-secondary); font-size: var(--fs-body); }

.status-badge {
    padding: 4px 10px;
    border-radius: 20px;
    font-size: var(--fs-caption);
    font-weight: 700;
    display: inline-flex;
    align-items: center;
    gap: 6px;
}

.status-badge.active { background: #ecfdf5; color: #10b981; }
.status-badge.expiring { background: #fffbeb; color: #d97706; }
.status-badge.inactive { background: #fef2f2; color: #ef4444; }

.status-dot { width: 6px; height: 6px; border-radius: 50%; background: currentColor; }

.row-actions { display: flex; justify-content: flex-end; gap: 8px; }

.action-btn {
    padding: 6px 12px;
    border-radius: 8px;
    background: #f8fafc;
    color: #0071C5;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 1px solid #e2e8f0;
    cursor: pointer;
    transition: all 0.2s;
    text-decoration: none;
    font-size: 0.8rem;
    font-weight: 700;
}

.action-btn:hover { border-color: #0071C5; background: white; }

.empty-state { padding: 4rem; text-align: center; color: #94a3b8; font-size: var(--fs-body-lg); font-weight: 500; }

@media (max-width: 640px) {
    .management-header { flex-direction: column; align-items: stretch; }
}
</style>
